import React, { useState, useRef } from 'react';

export const ProfileImageInput = ({
	field,
	form,
	label,
	required = false,
	accept = 'image/jpeg,image/jpg,image/png,image/gif',
	maxSize,
	className = '',
	size = 120,
	placeholder = 'A'
}) => {
	const [isHovering, setIsHovering] = useState(false);
	const [previewUrl, setPreviewUrl] = useState(null);
	const fileInputRef = useRef(null);

	const { name, value } = field;
	const { setFieldValue, setFieldTouched, errors, touched } = form;
	const error = touched[name] && errors[name];

	// Generate preview URL when file changes
	React.useEffect(() => {
		if (value && value instanceof File) {
			const url = URL.createObjectURL(value);
			setPreviewUrl(url);
			return () => URL.revokeObjectURL(url);
		} else {
			setPreviewUrl(null);
		}
	}, [value]);

	const isURLAbsolute = url => {
		return url.indexOf('://') > 0 || url.indexOf('//') === 0;
	};
	const buildAbsoluteURL = url => {
		return `${process.env.REACT_APP_API}${url}`;
	};

	const handleFileSelect = event => {
		const file = event.target.files[0];

		if (file) {
			const validTypes = accept.split(',').map(type => type.trim());
			if (!validTypes.includes(file.type)) {
				setFieldValue(name, null);
				setFieldTouched(name, true);
				return;
			}

			if (maxSize && file.size > maxSize) {
				setFieldValue(name, null);
				setFieldTouched(name, true);
				return;
			}

			setFieldValue(name, file);
			setFieldTouched(name, true);
		}
	};

	const handleClearImage = e => {
		e.stopPropagation();
		setFieldValue(name, null);
		setFieldTouched(name, true);
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	const handleClick = () => {
		if (fileInputRef.current) {
			fileInputRef.current.click();
		}
	};

	const renderContent = () => (
		<>
			{previewUrl || value ? (
				<img
					src={
						previewUrl ||
						(typeof value === 'string'
							? isURLAbsolute(value)
								? value
								: buildAbsoluteURL(value)
							: '')
					}
					alt="Profile"
					className="profile-image-preview"
				/>
			) : (
				<div className="profile-image-placeholder">
					<span className="placeholder-text">{placeholder}</span>
				</div>
			)}
			{isHovering && (
				<div className="profile-image-overlay">
					<div className="overlay-content">
						<img
							src="/ats/icons/camera-icon.svg"
							alt="Camera"
							className="overlay-icon"
						/>
						{previewUrl || value ? (
							<button
								type="button"
								className="clear-image-btn"
								onClick={handleClearImage}
								title="Clear image">
								Clear image
							</button>
						) : null}
					</div>
				</div>
			)}
		</>
	);

	return (
		<div className={`profile-image-input-wrapper ${className}`}>
			{label && (
				<label className="profile-image-label">
					{label}
					{required && <span className="required-indicator">*</span>}
				</label>
			)}

			<div
				className={`profile-image-input ${previewUrl || value ? 'has-image' : 'empty'} ${isHovering ? 'hovering' : ''}`}
				style={{ width: size, height: size }}
				onMouseEnter={() => setIsHovering(true)}
				onMouseLeave={() => setIsHovering(false)}
				onClick={handleClick}>
				{renderContent()}

				<input
					ref={fileInputRef}
					type="file"
					accept={accept}
					onChange={handleFileSelect}
					style={{ display: 'none' }}
					name={name}
				/>
			</div>

			{error && <div className="profile-image-error">{error}</div>}
		</div>
	);
};
